'use client';

import { motion } from 'motion/react';
import { useRef, useState, forwardRef, useEffect } from 'react';

import { pageTransitionDuration, CUSTOM_EASE } from '../../utils/animations';
import {
  getDynamicColorVars,
  getDynamicColorVarsByIndex,
  DYNAMIC_COLORS,
} from '../../utils/dynamicColors';

const TRANSITION_DURATION = 1.5;

// PROTOTYPE TOGGLE — compare two SPA-navigation entrance techniques:
//
//   true  → GPU-composited reveal. The incoming page slides up from below via
//           `transform: translateY` (runs on the compositor thread, so it keeps
//           gliding even when the main thread is busy mounting the new page).
//           The rounded-card look is kept with an animated border-radius +
//           overflow:hidden, and a subtle scale-up stands in for the old
//           side-margin inset. This is the fix for the mid-transition stall.
//
//   false → Original clip-path reveal (`inset(100vh 7% 0 round 200px)` morph).
//           Visually the reference, but clip-path is a MAIN-THREAD animation,
//           so it hitches when the incoming page's mount saturates the CPU.
//
// Flip this to feel both side by side. First loads render fully open in either
// mode — only SPA navigations differ.
const USE_TRANSFORM_REVEAL = true;

// Enter/exit are separate PageTransition instances (old page exiting, new
// page entering) that animate concurrently, so the body class can only come
// off once every one of them has actually finished. They're tracked by
// identity rather than with a +1/-1 counter because Motion does NOT fire
// onAnimationComplete for an animation it interrupts — tap a second link
// mid-transition and the entering page's `animate` is cut short by its own
// exit, giving two starts and one complete. A counter drifts upwards, never
// returns to zero, and `is-transitioning` stays stuck on body for the rest of
// the session — which pins the nav at opacity 0 (see .is-transitioning in
// NavigationMobile/NavigationDesktop.scss). A Set is idempotent, so the
// double start is harmless.
const activeTransitions = new Set();

function syncTransitionClass() {
  document.body.classList.toggle(
    'is-transitioning',
    activeTransitions.size > 0
  );
}

function PageTransition(
  { children, pageData, postType, isInitialLoad = false, isHome = false, path },
  ref
) {
  const [isAnimating, setIsAnimating] = useState(false);
  const hasCompletedFirstAnimation = useRef(false);
  const scrollOffsetRef = useRef(0);

  const [dynamicColorVars, setDynamicColorVars] = useState({});

  // Stable per-instance key for the activeTransitions set.
  const transitionId = useRef({});
  const transitionWatchdog = useRef(null);

  const endTransition = () => {
    clearTimeout(transitionWatchdog.current);
    activeTransitions.delete(transitionId.current);
    syncTransitionClass();
  };

  const beginTransition = () => {
    activeTransitions.add(transitionId.current);
    syncTransitionClass();
    // Belt and braces: onAnimationComplete also goes missing when the element
    // is unmounted mid-flight, and Motion's rAF loop stalls while the tab is
    // backgrounded (common on mobile — navigate, switch apps, come back).
    // Deliberately not cleared on unmount: this timer is what releases the
    // class for an instance that was removed before it ever completed.
    clearTimeout(transitionWatchdog.current);
    transitionWatchdog.current = setTimeout(
      endTransition,
      TRANSITION_DURATION * 1000 + 500
    );
  };

  // Keep the wrapper on its own GPU compositing layer for the whole
  // transition. `will-change` must be declared BEFORE the animation begins
  // (not as a keyframe target) so the browser promotes the layer ahead of
  // time, and it names clip-path too — the entrance's heaviest, main-thread
  // property. backface-visibility solidifies the layer promotion in Safari.
  // Released once the page settles (see onAnimationComplete) so the hint
  // doesn't linger and hurt scroll performance.
  const [layerActive, setLayerActive] = useState(true);

  let wrapperStyle = {
    backfaceVisibility: 'hidden',
    WebkitBackfaceVisibility: 'hidden',
    willChange: layerActive
      ? USE_TRANSFORM_REVEAL
        ? 'transform, border-radius'
        : 'transform, clip-path'
      : 'auto',
    // The transform reveal rounds its corners with border-radius, which only
    // clips child content when overflow is hidden. Applied for the duration of
    // the SPA entrance and released with the layer so it can't clip sticky/
    // fixed page content once settled.
    ...(USE_TRANSFORM_REVEAL && layerActive && !isInitialLoad
      ? { overflow: 'hidden' }
      : {}),
  };
  if (isAnimating) {
    wrapperStyle = {
      ...wrapperStyle,
      top: scrollOffsetRef.current,
      // The clip reveal's origin is tuned to the current scroll offset so the
      // clip window stays viewport-aligned. The transform reveal scales from a
      // fixed top-centre point instead — the scroll-relative 50vh origin would
      // otherwise land off-screen once the page is translated a viewport down.
      transformOrigin: USE_TRANSFORM_REVEAL
        ? 'center top'
        : `center calc(${scrollOffsetRef.current * -1}px + 50vh)`,
    };
  }

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
  }, []);

  // Apply dynamic colours from colorTheme
  useEffect(() => {
    const slug = pageData?.slug ?? path;
    setDynamicColorVars(
      getDynamicColorVars(pageData?.colorTheme, slug, isHome)
    );
  }, [pageData?.colorTheme, path, isHome]);

  // Keyboard testing: keys 1–5 rotate the starting colour
  useEffect(() => {
    const handleKey = (e) => {
      const index = parseInt(e.key, 10);
      if (index >= 1 && index <= DYNAMIC_COLORS.length) {
        setDynamicColorVars(getDynamicColorVarsByIndex(index - 1));
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  useEffect(() => {
    // Resume Lenis after the exit/entrance animation completes. Skipped on a
    // first load: nothing here stops Lenis on mount, so there's nothing to
    // resume — and HeroHome holds its own scroll lock for the homepage loader,
    // which this would otherwise release a few seconds early.
    if (isInitialLoad) return;

    const timer = setTimeout(() => {
      if (window.lenis) {
        window.lenis.start();
      }
    }, pageTransitionDuration * 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <motion.div
      className={`contentContainer`}
      style={{
        ...wrapperStyle,
        ...dynamicColorVars,
      }}
      initial={{
        // On a SPA nav the transform reveal starts slightly shrunk (stands in
        // for the old 7% side inset) and pushed a full viewport below; the
        // clip reveal instead starts fully open but clipped to a rounded card.
        // First loads start at full size and render fully open — nothing about
        // a hard load is animated here.
        scale: USE_TRANSFORM_REVEAL && !isInitialLoad ? 0.93 : 1,
        // Nudges Motion into emitting a matrix3d transform instead of a 2D
        // matrix, which forces GPU layer promotion (mainly a Safari fix).
        z: 0.001,
        // Transform reveal slides the page up from one viewport below
        // (compositor-driven); clip reveal keeps content near-stationary and
        // reveals it through a growing clip window.
        y: isInitialLoad ? '0px' : USE_TRANSFORM_REVEAL ? '100vh' : '150px',
        // Only SPA navigations get the slide-up-from-bottom entrance.
        ...(USE_TRANSFORM_REVEAL
          ? { borderRadius: isInitialLoad ? '0px' : '200px' }
          : {
              clipPath: isInitialLoad
                ? 'inset(0 round 0px)'
                : 'inset(100vh 7% 0 round 200px)',
            }),
      }}
      animate={{
        scale: 1,
        z: 0.001,
        y: '0px',
        ...(USE_TRANSFORM_REVEAL
          ? { borderRadius: '0px' }
          : { clipPath: 'inset(0vh 0 0 round 0px)' }),
        transition: {
          duration: TRANSITION_DURATION,
          ease: CUSTOM_EASE,
        },
      }}
      exit={{
        // scale: 1.05,
        y: -80,
        z: 0.01,
        zIndex: '-1',
        x: 0,
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        // transformOrigin: `center calc(${scrollOffsetRef.current * -1}px + 100vh)`,
        transition: {
          duration: TRANSITION_DURATION,
          ease: CUSTOM_EASE,
        },
      }}
      onAnimationStart={() => {
        if (hasCompletedFirstAnimation.current) {
          scrollOffsetRef.current = -window.scrollY;
          if (scrollOffsetRef.current !== 0) {
            window.scrollTo({ top: 0 });
          }
        }
        setIsAnimating(true);
        beginTransition();
      }}
      onAnimationComplete={() => {
        setIsAnimating(false);
        // Release the compositing-layer hint so the layer (and its memory)
        // is freed once the page is settled — keeping will-change on
        // permanently is wasteful and can itself hurt scroll performance.
        setLayerActive(false);
        scrollOffsetRef.current = 0;
        hasCompletedFirstAnimation.current = true;
        endTransition();
      }}
    >
      {children}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0 }}
        exit={{ opacity: 0.3 }}
        transition={{
          duration: TRANSITION_DURATION,
          ease: CUSTOM_EASE,
        }}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'black',
          pointerEvents: 'none',
          zIndex: 9999,
        }}
      />
    </motion.div>
  );
}

export default forwardRef(PageTransition);
